// src/pages/CityPage.jsx
//
// Per-city page template. Renders fully structured city data,
// supplemented by the parent state's cleaning calendar, regional
// contaminants (filtered by contaminantHighlights), and author byline.
//
// City data is split across two layers after session 36:
//   - src/data/cities/index.js carries the lightweight CITIES_INDEX
//     summary (used by the listing surfaces — sibling-cities grid,
//     cross-state nearby grid). One small shared chunk.
//   - src/data/cities/<stateSlug>.js carries the full record for every
//     city in that state. Loaded only when the focused city's full
//     fields (neighborhoodDetail, localQuirks, costRange, etc.) are
//     needed for the page body.
//
// All data modules are dynamic-imported so the main bundle stays lean.
// The /states/:stateSlug/:citySlug route only pulls the lightweight
// index + the focused state's per-state cities + states modules.
//
// Layout sections (top to bottom) — reordered s111 (Phase 3A) so the city's own
// authored prose and data come first and the state-level blocks come last:
//   1. Header / breadcrumb, H1, authored lead (cityAngles.js), intro (city.notes)
//   2. At-a-glance card + ZIP strip
//   3. Water: authored hardnessMeaning, then the table (hardness row first)
//   4. Climate (NOAA strip) + authored residueTiming + link to the state calendar
//   5. What's specific (localQuirks)
//   6. Housing age (Census ACS)
//   7. Neighborhoods
//   8. Cost + authored priceDrivers + authored diyVsPro
//   9. The one find-a-pro slot (AffiliateSlot)
//   10. Inherited filtered contaminants
//   11. FAQ (generateCityFaqs + FAQPage JSON-LD)
//   12. Sibling cities, then nearby cities across state lines
//   13. Compact author byline (state's assignedAuthor)
// Removed s111: the hero AffiliateSlot (duplicate) and the state's seasonal
// calendar (now a link from the climate section; it still renders on StatePage).

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AffiliateSlot from '../components/AffiliateSlot.jsx';
import { generateCityFaqs, faqJsonLd } from '../components/cityFaqs.js';
import Seo from '../components/Seo.jsx';
import WaterPanel from '../components/WaterPanel.jsx';
import { cityMeta } from '../data/cityAngles.js';
import { CITY_SUMMARIES } from '../data/citySummaries.js';
import { HOUSING_ERA } from '../data/housingEra.js';
import HousingEra from '../components/HousingEra.jsx';
import { CLIMATE } from '../data/climateNormals.js';
import ClimateStrip from '../components/ClimateStrip.jsx';
import { AIR_LOAD } from '../data/airLoad.js';
import AirPanel from '../components/AirPanel.jsx';
import MethodPanel from '../components/MethodPanel.jsx';
import { LOCAL_RULES, STATE_RULES } from '../data/localRules.js';
import LocalRules from '../components/LocalRules.jsx';
import { LOCAL_MARKET } from '../data/localMarket.js';
import MarketPanel from '../components/MarketPanel.jsx';
import { METRO_WAGE } from '../data/metroWage.js';
import { NEIGHBORHOOD_FACTS } from '../data/neighborhoodFacts.js';
import CITY_IMAGES from '../data/city-images.json'; // s140 Phase 7c (DECISIONS #72): Commons, location confirmed by the city's own category
import { breadcrumbSchema } from '../utils/schema.js';
import {
  hardnessLabel, hardnessColor,
  hardnessClause, hardnessFigure, hardnessIsPublished, hardnessProvenance,
} from '../data/water.js';

// Helper: slug-friendly anchor ids
function slugifyHeading(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Render a short prose string as React children, converting markdown
// inline link syntax `[text](href)` into <Link> elements. This is a
// minimal, deliberately-narrow parser — it handles only inline links,
// no bold/italic/emphasis/code/etc., because the city-data prose
// fields are plain editorial sentences and the only embeddable thing
// they ever carry is a sibling-city link injected by the
// inject-city-sibling-links.mjs generator (session 41 — pre-renamed
// from the v39 state-city script, see scripts/inject-state-city-links.mjs
// for the source-of-truth generator architecture).
//
// Internal links (starting with `/`) render as react-router <Link>
// so client-side navigation is preserved; anything else (rare for
// generator output, but safe) falls back to a plain anchor.
//
// Returns an array of React nodes that can be spread inside any
// element that expects text children, e.g.:
//   <p>{renderInlineLinks(city.notes)}</p>
function renderInlineLinks(text) {
  if (text == null) return null;
  const str = String(text);
  if (str.length === 0) return str;

  // Matches `[label](href)` where label has no `]` and href has no
  // `)` or whitespace. Matches what the generator emits and what any
  // hand-written prose would reasonably include.
  const re = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  const out = [];
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > lastIndex) {
      out.push(str.slice(lastIndex, m.index));
    }
    const [, label, href] = m;
    if (href.startsWith('/')) {
      out.push(
        <Link
          key={`il-${key++}`}
          to={href}
          style={{ color: 'inherit', borderBottom: '1px solid currentColor', textDecoration: 'none' }}
        >
          {label}
        </Link>
      );
    } else {
      out.push(
        <a
          key={`il-${key++}`}
          href={href}
          style={{ color: 'inherit', borderBottom: '1px solid currentColor', textDecoration: 'none' }}
        >
          {label}
        </a>
      );
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < str.length) {
    out.push(str.slice(lastIndex));
  }
  // If no links matched, return the original string for parity with
  // the old `{text}` render path (single text node, no array wrap).
  return out.length === 0 ? str : out;
}

// Regions where neighboring-state cities cluster tightly enough that the
// default cap of 6 cross-state nearby cards is restrictive. In the
// Mid-Atlantic / Northeast / New England corridor, a focused city in
// e.g. New Jersey can plausibly cross-link to 8+ peer cities within
// ~50 miles across NY, PA, DE, and CT — limiting to 6 silently drops
// real reader value. For everywhere else (Mountain West, Plains, the
// vast interior West) 6 already saturates the realistic nearby set.
//
// The list mirrors the `region` tag on STATES_INDEX entries, not a
// hand-maintained slug list — so a future region re-tag automatically
// adjusts which states get the raised cap without code change here.
const DENSE_NEARBY_REGIONS = new Set(['Mid-Atlantic', 'Northeast', 'New England']);
const NEARBY_CROSS_STATE_LIMIT_DEFAULT = 6;
const NEARBY_CROSS_STATE_LIMIT_DENSE = 8;

// Hardness tier label + pill colour. Both come from src/data/water.js — the
// single source of truth for bands site-wide (s73). The local copies that
// used to live here spoke a vocabulary one notch softer than USGS across the
// whole range, so a 200 mg/L city read "hard" here and "Very Hard" in the
// Hard Water Scorer. Lowercase because every use below is mid-sentence prose.
function hardnessTier(h) {
  return hardnessLabel(h, { lower: true });
}

function tierColor(h, T) {
  return hardnessColor(h, T);
}

// Source label display — for the at-a-glance card.
// s109: the phrase used inside 'delivers water to X from …' — sourceDisplay() is a
// label ('Mixed source', 'Surface (lake/reservoir)') and read badly mid-sentence.
function sourcePhrase(s) {
  switch (s) {
    case 'surface': return 'lake or reservoir water';
    case 'groundwater': return 'groundwater';
    case 'aquifer': return 'an aquifer';
    case 'mixed': return 'a mix of surface water and groundwater';
    case 'well-prevalent': return 'mostly private wells';
    default: return 'its sources';
  }
}

function sourceDisplay(s) {
  switch (s) {
    case 'surface': return 'Surface (lake/reservoir)';
    case 'groundwater': return 'Groundwater';
    case 'aquifer': return 'Aquifer';
    case 'mixed': return 'Mixed source';
    case 'well-prevalent': return 'Private well predominant';
    default: return s || '—';
  }
}

export default function CityPage() {
  const { stateSlug, citySlug } = useParams();

  const [dark, setDark] = useState(false);
  const [city, setCity] = useState(null);
  const [stateData, setStateData] = useState(null);
  const [author, setAuthor] = useState(null);
  const [siblingCities, setSiblingCities] = useState([]);
  const [nearbyCrossStateCities, setNearbyCrossStateCities] = useState([]);
  const [allStatesIndex, setAllStatesIndex] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCity(null);
    setStateData(null);
    setAuthor(null);
    setSiblingCities([]);
    setNearbyCrossStateCities([]);
    setAllStatesIndex(null);
    setNotFound(false);
    window.scrollTo(0, 0);

    if (!stateSlug || !citySlug) {
      setNotFound(true);
      return;
    }

    // Dynamic-import data modules in parallel. Each gets split into
    // its own Vite chunk, so the main bundle does not pay for any.
    // After session 36, the cities monolith is split too — we load the
    // lightweight CITIES_INDEX from cities/index.js and only the
    // focused state's per-state cities module for the full record.
    Promise.all([
      import('../data/cities/index.js'),
      import('../data/states/index.js'),
      import('../data/contributors.js'),
      import('../data/stateAdjacency.js'),
    ])
      .then(([citiesIndexMod, statesIndexMod, contribMod, adjMod]) => {
        if (cancelled) return;
        // Fast-path 404: if the lightweight cities index has no entry
        // for this (stateSlug, citySlug) pair, no dedicated city page
        // exists. Bail before kicking off the per-state module fetch.
        const citySummary = citiesIndexMod.getCitySummaryBySlug(stateSlug, citySlug);
        if (!citySummary) {
          setNotFound(true);
          return;
        }
        // Look up the parent state's lightweight summary first. This
        // gives us the assignedAuthor without paying for the full per-
        // state record yet — useful because the author lookup needs
        // to happen synchronously with this Promise.all's callback.
        const summary = statesIndexMod.getStateSummaryBySlug(stateSlug);
        // Author lookup via state's assignedAuthor
        if (summary?.assignedAuthor && contribMod.CONTRIBUTORS) {
          const a = contribMod.CONTRIBUTORS.find((c) => c.slug === summary.assignedAuthor);
          setAuthor(a || null);
        }
        // Async-load the focused city's full record. The per-state
        // cities module contains all cities for the state; we pluck the
        // matching entry. Until it resolves `city` stays null and the
        // page shows its loading state — same invariant as v35's
        // stateData arrives-one-tick-late pattern.
        citiesIndexMod.loadCityBySlug(stateSlug, citySlug).then((full) => {
          if (cancelled) return;
          if (!full) {
            setNotFound(true);
            return;
          }
          setCity(full);
        });
        // Async-load the full state record for cleaningCalendar +
        // state.name (used in the city page seasonal calendar copy).
        statesIndexMod.loadStateBySlug(stateSlug).then((full) => {
          if (cancelled) return;
          setStateData(full || null);
        });
        // Siblings — other cities in the same state, excluding this
        // one. The listing cards render only fields the lightweight
        // index already carries (cityName, hardness, populationK,
        // firstZip), so no per-state module load is needed for this.
        const sibs = citiesIndexMod
          .getCitySummariesByState(stateSlug)
          .filter((c) => c.citySlug !== citySlug);
        setSiblingCities(sibs);
        // Cross-state nearby cities — for SEO depth across borders.
        // Runs entirely on CITIES_INDEX + STATE_ADJACENCY; no per-
        // state module load is needed.
        //
        // The card cap is region-aware: dense corridor states (Mid-
        // Atlantic / Northeast / New England) get 8, everywhere else
        // stays at the default 6. The summary lookup is already in
        // hand from a few lines up — we just read `region` off it.
        const nearbyLimit = summary && DENSE_NEARBY_REGIONS.has(summary.region)
          ? NEARBY_CROSS_STATE_LIMIT_DENSE
          : NEARBY_CROSS_STATE_LIMIT_DEFAULT;
        const nearby = citiesIndexMod.getNearbyCitySummariesAcrossStates(
          stateSlug,
          citySlug,
          adjMod.STATE_ADJACENCY || {},
          nearbyLimit,
        );
        setNearbyCrossStateCities(nearby);
        // Build a lookup of state slug → display name for label rendering.
        // The lightweight index has name on every entry — no need to
        // load full per-state modules.
        const idx = {};
        if (statesIndexMod.STATES_INDEX) {
          for (const st of statesIndexMod.STATES_INDEX) {
            idx[st.slug] = st.name;
          }
        }
        setAllStatesIndex(idx);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });

    return () => { cancelled = true; };
  }, [stateSlug, citySlug]);

  const T = dark
    ? { bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A' }
    : { bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A' };

  const fontDisplay = "'Fraunces', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  // 404-style state
  if (notFound) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontDisplay, fontSize: 24, fontStyle: 'italic',
        padding: 40,
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: T.inkFaint, marginBottom: 24 }}>City page not found.</p>
          <Link to="/states" style={{
            color: T.sienna, textDecoration: 'none',
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
            fontStyle: 'normal',
          }}>
            ← BROWSE ALL STATES
          </Link>
        </div>
      </div>
    );
  }

  // Loading state — while dynamic chunks resolve
  if (!city) {
    return (
      <div style={{
        minHeight: '100vh', background: T.bg, color: T.inkFaint,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
      }}>
        LOADING CITY PROFILE…
      </div>
    );
  }

  const faqs = generateCityFaqs(city, stateData);
  const faqJson = faqJsonLd(faqs);

  // Filter parent-state contaminants to ones tagged by the city's
  // contaminantHighlights. If the highlights match name fragments, surface
  // them; otherwise fall back to showing all state contaminants but capped.
  const cityContaminants = (() => {
    const all = stateData?.regionalContaminants || [];
    const highlights = city.contaminantHighlights || [];
    if (!highlights.length || !all.length) return all.slice(0, 3);
    const matched = all.filter((c) =>
      highlights.some((h) => c.name.toLowerCase().includes(h.toLowerCase().split(' ')[0])
        || h.toLowerCase().includes(c.name.toLowerCase().split(' ')[0])),
    );
    return matched.length ? matched : all.slice(0, 3);
  })();

  // s110: title, meta description and lead come from one authored source.
  const meta = cityMeta(city, stateData?.name);
  // s111 Phase 3A.3: the authored plain-English summary, where written, sits
  // under the lead; the notes paragraph then opens the What's-specific section.
  const summary = CITY_SUMMARIES[`${stateSlug}/${citySlug}`] || null;

  // ------- Render -------
  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.ink,
      fontFamily: fontBody,
    }}>
      <Seo
        title={meta.title}
        description={meta.description}
        path={`/states/${stateSlug}/${citySlug}`}
        ogType="article"
        jsonLd={[
          faqJson,
          breadcrumbSchema([
            { name: 'Guide', path: '/' },
            { name: 'States', path: '/states' },
            { name: stateData?.name || stateSlug, path: `/states/${stateSlug}` },
            { name: city?.cityName || citySlug, path: `/states/${stateSlug}/${citySlug}` },
          ]),
        ]}
      />

      <div style={{
        maxWidth: 980, margin: '0 auto',
        padding: '32px 24px 64px',
      }}>
        {/* Theme toggle */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 32,
        }}>
          <Link to="/" style={{
            color: T.sienna, textDecoration: 'none',
            fontFamily: fontMono, fontSize: 11, letterSpacing: '0.18em',
          }}>
            WINDOW WASHING GUIDE
          </Link>
          <button
            onClick={() => setDark((d) => !d)}
            style={{
              background: 'transparent', border: `1px solid ${T.rule}`,
              color: T.inkSoft, padding: '6px 12px',
              fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em',
              cursor: 'pointer',
            }}
          >
            {dark ? '☀ LIGHT' : '☾ DARK'}
          </button>
        </div>

        {/* Breadcrumb */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.08em', color: T.inkFaint,
          marginBottom: 24,
        }}>
          <Link to="/states" style={{ color: T.inkFaint, textDecoration: 'none' }}>
            STATES
          </Link>
          {' / '}
          <Link
            to={`/states/${stateSlug}`}
            style={{ color: T.inkFaint, textDecoration: 'none' }}
          >
            {stateData?.name?.toUpperCase() || stateSlug.toUpperCase()}
          </Link>
          {' / '}
          <span style={{ color: T.ink }}>{city.cityName.toUpperCase()}</span>
        </div>

        {/* Section label */}
        <div style={{
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.18em', color: T.sienna,
          marginBottom: 18,
        }}>
          CITY PROFILE
          {city.metro && (
            <>
              &nbsp;&nbsp;·&nbsp;&nbsp; {city.metro.toUpperCase()}
            </>
          )}
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: fontDisplay,
          fontSize: 'clamp(40px, 6vw, 64px)',
          fontWeight: 400, lineHeight: 1.05,
          letterSpacing: '-0.025em',
          margin: '0 0 24px', color: T.ink,
        }}>
          Window Washing in {city.cityName}
        </h1>

        {/* s110 Phase 3A.2 — the city's own lead, authored in src/data/cityAngles.js
            (also its meta description). Leads with what is distinctive here. */}
        {meta.dek && (
          <p style={{
            fontFamily: fontDisplay, fontSize: 'clamp(19px, 2.4vw, 23px)',
            lineHeight: 1.4, color: T.ink, margin: '-8px 0 22px', maxWidth: 760,
          }}>
            {meta.dek}
          </p>
        )}

        {/* Intro paragraph — the city's own notes. s110 Phase 3A cut the generated
            "{City} runs on {source} from {utility} at {hardness}" opener that led
            every page: the authored lead above now opens the page, and the same
            facts stay in the at-a-glance card and the water-profile paragraph. */}
        {(summary || city.notes) && (
          <p data-region="intro" style={{
            fontFamily: fontDisplay, fontSize: 22,
            lineHeight: 1.5, color: T.ink,
            margin: '0 0 32px', fontStyle: 'italic',
            fontWeight: 400,
          }}>
            {summary ? summary : renderInlineLinks(city.notes)}
          </p>
        )}

        {/* s140 Phase 7c — verified-or-nothing city photo (DECISIONS #72). Below the fold on phones: lazy. */}
        {CITY_IMAGES[`${stateSlug}--${citySlug}`] && (() => { const ci = CITY_IMAGES[`${stateSlug}--${citySlug}`]; return (
          <figure data-region="city-hero" style={{ margin: '0 0 32px' }}>
            <img src={ci.hero} srcSet={`${ci.thumb} 480w, ${ci.hero} 1200w`} sizes="(max-width: 800px) 100vw, 760px"
              alt={ci.alt} width={1200} height={800} loading="lazy" decoding="async"
              style={{ width: '100%', height: 'auto', aspectRatio: '3 / 2', objectFit: 'cover', display: 'block', border: `1px solid ${T.rule}` }} />
            <figcaption style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: '0.12em', color: T.inkFaint, marginTop: 8, textAlign: 'right' }}>
              PHOTO <a href={ci.creditUrl} target="_blank" rel="noopener noreferrer nofollow" style={{ color: T.inkFaint, textDecoration: 'none' }}>{ci.credit.toUpperCase()}</a>
              {' / WIKIMEDIA COMMONS · '}{ci.license.toUpperCase()}
            </figcaption>
          </figure>
        ); })()}

        {/* At-a-glance card */}
        <div style={{
          border: `1px solid ${T.rule}`,
          borderTop: `2px solid ${T.sienna}`,
          padding: '24px 24px 16px',
          background: T.surface,
          marginBottom: 32,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 20,
        }}>
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 9,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 6,
            }}>
              HARDNESS
            </div>
            <div style={{
              fontFamily: fontDisplay, fontSize: 28,
              fontWeight: 500, color: tierColor(city.hardness, T),
              lineHeight: 1,
            }}>
              {hardnessFigure(city)}
            </div>
            <div style={{
              fontFamily: fontMono, fontSize: 10,
              color: T.inkFaint, marginTop: 4,
            }}>
              {hardnessIsPublished(city)
                ? <>mg/L · {hardnessTier(city.hardness)}</>
                : <>OPERATOR PUBLISHES NO FIGURE</>}
            </div>
          </div>
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 9,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 6,
            }}>
              SOURCE
            </div>
            <div style={{
              fontFamily: fontBody, fontSize: 15,
              fontWeight: 500, color: T.ink,
              lineHeight: 1.3,
            }}>
              {sourceDisplay(city.sourceType)}
            </div>
          </div>
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 9,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 6,
            }}>
              UTILITY
            </div>
            <div style={{
              fontFamily: fontBody, fontSize: 15,
              fontWeight: 500, color: T.ink,
              lineHeight: 1.3,
            }}>
              {city.utility}
            </div>
          </div>
          <div>
            <div style={{
              fontFamily: fontMono, fontSize: 9,
              letterSpacing: '0.18em', color: T.inkFaint,
              marginBottom: 6,
            }}>
              POPULATION
            </div>
            <div style={{
              fontFamily: fontDisplay, fontSize: 22,
              fontWeight: 500, color: T.ink,
              lineHeight: 1.1,
            }}>
              {city.populationK}k
            </div>
            {city.elevation && (
              <div style={{
                fontFamily: fontMono, fontSize: 10,
                color: T.inkFaint, marginTop: 4,
              }}>
                {city.elevation} ft elev.
              </div>
            )}
          </div>
        </div>

        {/* ZIPs strip — each ZIP is a deep-link into the Hard Water Scorer
            tool, prefilled and auto-run. We surface the actual list rather
            than a single CTA so a reader can pick the ZIP closest to their
            own neighborhood, which lands them on the right ZIP3 lookup. The
            tool clears the ?zip= query param on consume so the back button
            returns here cleanly. */}
        {city.zips && city.zips.length > 0 && (
          <div style={{
            fontFamily: fontMono, fontSize: 10,
            letterSpacing: '0.08em', color: T.inkFaint,
            marginBottom: 32,
          }}>
            <span style={{ color: T.inkFaint }}>SCORE YOUR ZIP:</span>{' '}
            {city.zips.map((z, i) => (
              <span key={z}>
                <Link
                  to={`/tools/hard-water-scorer?zip=${z}`}
                  aria-label={`Score water hardness for ZIP ${z}`}
                  style={{
                    color: T.sienna,
                    textDecoration: 'none',
                    borderBottom: `1px dotted ${T.sienna}`,
                    paddingBottom: 1,
                  }}
                >
                  {z}
                </Link>
                {i < city.zips.length - 1 ? (
                  <span style={{ color: T.inkSoft }}> · </span>
                ) : null}
              </span>
            ))}
          </div>
        )}

        {/* Water profile prose */}
        <div style={{ marginTop: 56 }}>
          <div style={{
            fontFamily: fontMono, fontSize: 11,
            letterSpacing: '0.18em', color: T.sienna,
            marginBottom: 14,
          }}>
            WATER PROFILE
          </div>
          <h2
            id={slugifyHeading('what the water means for glass')}
            style={{
              fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
              lineHeight: 1.15, letterSpacing: '-0.02em',
              margin: '0 0 18px', color: T.ink,
            }}
          >
            What the water means for the glass
          </h2>
          {/* s111 Phase 3A: the city's own authored explanation replaces the generated
              "{utility} delivers water to {city} from … at …" sentence that led this
              section on all 271 pages. The figure, band and provenance moved into the
              table below as its first row (hardnessRow in water.js). */}
          {city.hardnessMeaning && (
            <p data-region="water-meaning" style={{
              fontFamily: fontBody, fontSize: 17,
              lineHeight: 1.7, color: T.inkSoft,
              margin: '0 0 8px', maxWidth: 720,
            }}>
              {renderInlineLinks(city.hardnessMeaning)}
            </p>
          )}
          <WaterPanel
            city={city} T={T}
            fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
            headingId={slugifyHeading('tap water by the numbers')}
          />
          {/* s133 Phase 5a — method verdicts computed from the figures above (DECISIONS #45) */}
          <MethodPanel city={city} climate={CLIMATE[`${stateSlug}/${citySlug}`]} T={T} fontBody={fontBody} fontMono={fontMono} />
        </div>

        {/* s110 Phase 3B-3b — NOAA 1991–2020 normals, nearest station */}
        <ClimateStrip
          climate={CLIMATE[`${stateSlug}/${citySlug}`]} air={AIR_LOAD[`${stateSlug}/${citySlug}`]} T={T}
          fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
          headingId={slugifyHeading('when a wash holds')}
        />
        {/* s111 Phase 3A: the authored "what sets the cleaning clock" paragraph used to
            appear only inside a collapsed FAQ answer. It sits under the climate data it
            interprets, and the state-wide seasonal calendar (identical on every city in
            a state, and sometimes at odds with the city's own NOAA months) is now a link. */}
        {(city.residueTiming || stateData?.cleaningCalendar) && (
          <div style={{ marginTop: 28 }}>
            {city.residueTiming && (
              <>
                <h3 style={{
                  fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
                  letterSpacing: '-0.01em', margin: '0 0 8px', color: T.ink,
                }}>
                  What sets the cleaning clock
                </h3>
                <p data-region="residue-timing" style={{
                  fontFamily: fontBody, fontSize: 16,
                  lineHeight: 1.7, color: T.inkSoft,
                  margin: '0 0 12px', maxWidth: 720,
                }}>
                  {renderInlineLinks(city.residueTiming)}
                </p>
              </>
            )}
            {stateData?.cleaningCalendar && (
              <Link
                to={`/states/${stateSlug}#the-year-in-seasons`}
                style={{
                  fontFamily: fontMono, fontSize: 11, letterSpacing: '0.14em',
                  color: T.sienna, textDecoration: 'none',
                  borderBottom: `1px dotted ${T.sienna}`, paddingBottom: 1,
                }}
              >
                {(stateData.name || '').toUpperCase()} SEASON BY SEASON →
              </Link>
            )}
          </div>
        )}

        {/* Local quirks */}
        {city.localQuirks && city.localQuirks.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              WHAT'S DISTINCTIVE
            </div>
            <h2
              id={slugifyHeading('local quirks')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              What's specific to {city.cityName}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 14,
            }}>
              {summary && city.notes && (
                <p data-region="notes" style={{
                  fontFamily: fontBody, fontSize: 16, lineHeight: 1.7,
                  color: T.inkSoft, margin: '0 0 4px', maxWidth: 720,
                }}>
                  {renderInlineLinks(city.notes)}
                </p>
              )}
              {city.localQuirks.map((q, i) => (
                <div
                  key={i}
                  style={{
                    borderLeft: `2px solid ${T.sienna}`,
                    padding: '4px 0 4px 20px',
                  }}
                >
                  <p style={{
                    fontFamily: fontDisplay, fontSize: 18,
                    lineHeight: 1.55, color: T.ink, margin: 0,
                    fontStyle: 'italic',
                  }}>
                    {renderInlineLinks(q)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* s110 Phase 3B-3a — housing age from Census ACS year-built counts */}
        <HousingEra
          housing={HOUSING_ERA[`${stateSlug}/${citySlug}`]} T={T}
          fontDisplay={fontDisplay} fontBody={fontBody} fontMono={fontMono}
          headingId={slugifyHeading('how old the glass is')}
        />

        {/* Neighborhoods grid */}
        {city.neighborhoodDetail && city.neighborhoodDetail.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              NEIGHBORHOODS
            </div>
            <h2
              id={slugifyHeading('the city by neighborhood')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              The city, by neighborhood
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 14,
            }}>
              {city.neighborhoodDetail.map((n) => (
                <div
                  key={n.name}
                  style={{
                    border: `1px solid ${T.rule}`,
                    padding: '16px 18px',
                    background: T.bg,
                  }}
                >
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 17,
                    fontWeight: 500, color: T.ink,
                    marginBottom: 6,
                  }}>
                    {n.name}
                  </div>
                  <div style={{
                    fontFamily: fontBody, fontSize: 13,
                    lineHeight: 1.55, color: T.inkSoft,
                  }}>
                    {renderInlineLinks(n.note)}
                  </div>
                  {/* s127 Phase 4c-2: one sourced fact per neighbourhood (src/data/neighborhoodFacts.js) */}
                  {(() => { const f = NEIGHBORHOOD_FACTS[`${stateSlug}/${citySlug}`]?.[n.name]; if (f?.s !== 'captured') return null; const [y, m] = f.asOf.split('-');
                    return (
                      <div data-nfact={n.name} style={{ fontFamily: fontBody, fontSize: 12.5, lineHeight: 1.55, color: T.ink, marginTop: 10, paddingTop: 8, borderTop: `1px dashed ${T.rule}` }}>
                        {f.text}{' '}
                        <span style={{ fontFamily: fontMono, fontSize: 10.5, color: T.inkSoft }}>
                          {/* lower-case lead-in keeps the source line inside the fact's own sentence for the share gate */}
                          via {f.sources.map((src, i) => (<React.Fragment key={src.url}>{i ? ' · ' : ''}<a href={src.url} rel="noopener" target="_blank" style={{ color: 'inherit' }}>{src.title}</a></React.Fragment>))}, checked {new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', { month: 'short' })} {y}
                        </span>
                      </div>
                    ); })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Local cost range */}
        {city.costRange && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              WHAT IT COSTS
            </div>
            <h2
              id={slugifyHeading('local cost range')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              What window cleaning costs in {city.cityName}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
              marginBottom: 16,
            }}>
              {city.costRange.perPane && (
                <div style={{
                  background: T.surface,
                  borderTop: `2px solid ${T.sienna}`,
                  padding: '18px 20px',
                }}>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 8,
                  }}>
                    PER PANE
                  </div>
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 26,
                    fontWeight: 500, color: T.ink,
                  }}>
                    ${city.costRange.perPane[0]}–${city.costRange.perPane[1]}
                  </div>
                </div>
              )}
              {city.costRange.wholeHome && (
                <div style={{
                  background: T.surface,
                  borderTop: `2px solid ${T.sienna}`,
                  padding: '18px 20px',
                }}>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 8,
                  }}>
                    WHOLE HOME EXT.
                  </div>
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 26,
                    fontWeight: 500, color: T.ink,
                  }}>
                    ${city.costRange.wholeHome[0]}–${city.costRange.wholeHome[1]}
                  </div>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    color: T.inkFaint, marginTop: 4,
                  }}>
                    single-story baseline
                  </div>
                </div>
              )}
              {city.costTier && (
                <div style={{
                  background: T.surface,
                  borderTop: `2px solid ${T.sienna}`,
                  padding: '18px 20px',
                }}>
                  <div style={{
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 8,
                  }}>
                    MARKET TIER
                  </div>
                  <div style={{
                    fontFamily: fontDisplay, fontSize: 22,
                    fontWeight: 500, color: T.ink,
                    textTransform: 'capitalize',
                  }}>
                    {city.costTier}
                  </div>
                </div>
              )}
            </div>
            <p style={{
              fontFamily: fontBody, fontSize: 14,
              lineHeight: 1.6, color: T.inkSoft,
              margin: '0 0 18px',
            }}>
              {city.priceDrivers
                || 'Story height, screen condition, frame material, and route density move the actual quote.'}
            </p>
            {/* s127 Phase 4c-1: the housing and cleaning trade around the city, from the Census */}
            <MarketPanel market={LOCAL_MARKET[`${stateSlug}/${citySlug}`]} wage={METRO_WAGE[`${stateSlug}/${citySlug}`]} T={T} fontBody={fontBody} fontMono={fontMono} />
            {/* s111: the authored DIY-vs-pro paragraph, previously only a collapsed FAQ answer */}
            {city.diyVsPro && (
              <>
                <h3 style={{
                  fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
                  letterSpacing: '-0.01em', margin: '8px 0 8px', color: T.ink,
                }}>
                  Doing it yourself
                </h3>
                <p data-region="diy-vs-pro" style={{
                  fontFamily: fontBody, fontSize: 16,
                  lineHeight: 1.7, color: T.inkSoft,
                  margin: '0 0 18px', maxWidth: 720,
                }}>
                  {renderInlineLinks(city.diyVsPro)}
                </p>
              </>
            )}
            <Link
              to="/tools/cost-estimator"
              style={{
                display: 'inline-block',
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
                textDecoration: 'none',
                border: `1px solid ${T.sienna}`,
                padding: '10px 18px',
              }}
            >
              OPEN COST ESTIMATOR →
            </Link>
          </div>
        )}

        {/* s111: the one find-a-pro slot, after costs (it used to print twice: under the hero and at the foot) */}
        <div style={{ marginTop: 56 }}>
          <AffiliateSlot
            variant="state"
            location={`${city.cityName}, ${stateData?.name || ''}`.trim().replace(/,$/, '')}
            theme={T}
            fontDisplay={fontDisplay}
            fontBody={fontBody}
            fontMono={fontMono}
          />
        </div>

        {/* s112 Phase 4a: measured particulate from the nearest EPA monitor */}
        <AirPanel air={AIR_LOAD[`${stateSlug}/${citySlug}`]} T={T} fontBody={fontBody} fontMono={fontMono} />
        {/* s112 Phase 4b: local rules, each from the city's or utility's own page */}
        <LocalRules rules={LOCAL_RULES[`${stateSlug}/${citySlug}`]} stateRule={STATE_RULES[stateSlug]?.height} stateSlug={stateSlug} stateName={stateData?.name || stateSlug} T={T} fontBody={fontBody} fontMono={fontMono} />

        {/* Filtered contaminants */}
        {cityContaminants && cityContaminants.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              WHAT GETS ON THE GLASS
            </div>
            <h2
              id={slugifyHeading('contaminants you will actually see')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              What actually shows up on {city.cityName} glass
            </h2>
            <div style={{ borderTop: `1px solid ${T.rule}` }}>
              {cityContaminants.map((c, i) => (
                <div
                  key={c.name}
                  style={{
                    padding: '20px 0',
                    borderBottom: i < cityContaminants.length - 1
                      ? `1px solid ${T.rule}` : 'none',
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 16, flexWrap: 'wrap',
                    marginBottom: 8,
                  }}>
                    <div style={{
                      fontFamily: fontDisplay, fontSize: 19,
                      fontWeight: 500, color: T.ink,
                    }}>
                      {c.name}
                    </div>
                    <div style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.12em', color: T.inkFaint,
                    }}>
                      {c.season?.toUpperCase()}
                    </div>
                  </div>
                  <p style={{
                    fontFamily: fontBody, fontSize: 14,
                    lineHeight: 1.6, color: T.inkSoft, margin: 0,
                  }}>
                    {c.notes}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ */}
        {faqs && faqs.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              QUESTIONS WE GET
            </div>
            <h2
              id={slugifyHeading('frequently asked')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              {/* s134: was "Common questions about window cleaning in X" — seven words of
                  boilerplate on 269 pages under an eyebrow that already says QUESTIONS WE GET. */}
              {city.cityName}, answered
            </h2>
            <div>
              {faqs.map((f, i) => (
                <details
                  key={i}
                  style={{
                    borderTop: i === 0 ? `1px solid ${T.rule}` : 'none',
                    borderBottom: `1px solid ${T.rule}`,
                    padding: '16px 0',
                  }}
                >
                  <summary style={{
                    fontFamily: fontDisplay, fontSize: 17,
                    fontWeight: 500, color: T.ink,
                    cursor: 'pointer', listStyle: 'none',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 12,
                  }}>
                    <span>{f.q}</span>
                    <span style={{
                      color: T.sienna, fontFamily: fontMono,
                      fontSize: 13,
                    }}>＋</span>
                  </summary>
                  <p style={{
                    fontFamily: fontBody, fontSize: 14,
                    lineHeight: 1.65, color: T.inkSoft,
                    margin: '14px 0 4px',
                  }}>
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Sibling cities */}
        {siblingCities && siblingCities.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              ELSEWHERE IN {stateData?.name?.toUpperCase() || 'THIS STATE'}
            </div>
            <h2
              id={slugifyHeading('other cities')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 24px', color: T.ink,
              }}
            >
              Other cities we cover in {stateData?.name || 'this state'}
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 10,
            }}>
              {siblingCities.map((sc) => {
                // sc is a lightweight CITIES_INDEX entry, so it carries
                // `firstZip` (a single string) rather than the full
                // `zips` array. CityPage card surfaces only ever read
                // zips[0], so the indexed shape is lossless for this UI.
                const scZip = sc.firstZip || null;
                return (
                  <div
                    key={sc.citySlug}
                    style={{
                      border: `1px solid ${T.rule}`,
                      background: T.bg,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Link
                      to={`/states/${sc.stateSlug}/${sc.citySlug}`}
                      style={{
                        padding: '14px 16px 12px',
                        textDecoration: 'none',
                        color: T.ink,
                        display: 'block',
                      }}
                    >
                      <div style={{
                        fontFamily: fontDisplay, fontSize: 16,
                        fontWeight: 500, color: T.ink, marginBottom: 4,
                      }}>
                        {sc.cityName}
                      </div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        color: T.inkFaint, letterSpacing: '0.05em',
                      }}>
                        {hardnessFigure(sc, { withUnit: true })} · pop. {sc.populationK}k
                      </div>
                    </Link>
                    {/* ZIP3 deep-link into HardWaterScorer for the sibling
                        city. Same structural pattern as the cross-state
                        cards: sibling anchor, not nested. */}
                    {scZip && (
                      <Link
                        to={`/tools/hard-water-scorer?zip=${scZip}`}
                        aria-label={`Score water hardness for ${sc.cityName} (ZIP ${scZip})`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 16px 10px',
                          borderTop: `1px solid ${T.rule}`,
                          fontFamily: fontMono, fontSize: 9,
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          color: T.sienna,
                          textDecoration: 'none',
                        }}
                      >
                        <span>Score {scZip}</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 18 }}>
              <Link
                to={`/states/${stateSlug}`}
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  textDecoration: 'none',
                }}
              >
                ← BACK TO {stateData?.name?.toUpperCase() || 'STATE'} OVERVIEW
              </Link>
            </div>
          </div>
        )}

        {/* Nearby cities across state borders.  We surface up to 6 cities
            from land-adjacent states so a reader of the Charlotte page can
            jump straight to Greenville (SC) or Knoxville (TN) without
            backing out to the state index. Each card labels the state
            so the reader knows they are crossing a border. */}
        {nearbyCrossStateCities && nearbyCrossStateCities.length > 0 && (
          <div style={{ marginTop: 56 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 14,
            }}>
              ACROSS THE BORDER
            </div>
            <h2
              id={slugifyHeading('nearby cities in neighboring states')}
              style={{
                fontFamily: fontDisplay, fontSize: 28, fontWeight: 400,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 8px', color: T.ink,
              }}
            >
              Nearby cities in neighboring states
            </h2>
            <p style={{
              fontFamily: fontBody, fontSize: 14,
              lineHeight: 1.55, color: T.inkSoft,
              margin: '0 0 22px',
              maxWidth: 620,
            }}>
              Just over the {stateData?.name || 'state'} line
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 10,
            }}>
              {nearbyCrossStateCities.map((nc) => {
                const stateName = (allStatesIndex && allStatesIndex[nc.stateSlug])
                  || nc.stateSlug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                // nc is a lightweight CITIES_INDEX entry — firstZip
                // (single string) is the only ZIP surface this card
                // renders, so the indexed shape is lossless here too.
                const ncZip = nc.firstZip || null;
                return (
                  <div
                    key={`${nc.stateSlug}-${nc.citySlug}`}
                    style={{
                      border: `1px solid ${T.rule}`,
                      background: T.bg,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Primary card-body Link — takes the reader to the
                        neighbor city page. Kept as the full-bleed clickable
                        region so the card-wide tap affordance is preserved
                        for the common case (read the neighbor's full page). */}
                    <Link
                      to={`/states/${nc.stateSlug}/${nc.citySlug}`}
                      style={{
                        padding: '14px 16px 12px',
                        textDecoration: 'none',
                        color: T.ink,
                        display: 'block',
                      }}
                    >
                      <div style={{
                        fontFamily: fontDisplay, fontSize: 16,
                        fontWeight: 500, color: T.ink, marginBottom: 4,
                      }}>
                        {nc.cityName}
                      </div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        color: T.inkFaint, letterSpacing: '0.05em',
                        marginBottom: 4,
                      }}>
                        {stateName.toUpperCase()}
                      </div>
                      <div style={{
                        fontFamily: fontMono, fontSize: 10,
                        color: T.inkFaint, letterSpacing: '0.05em',
                      }}>
                        {hardnessFigure(nc, { withUnit: true })} · pop. {nc.populationK}k
                      </div>
                    </Link>
                    {/* Secondary deep-link into HardWaterScorer with the
                        neighbor city's flagship ZIP. Rendered as a sibling
                        anchor (not nested inside the card Link, which would
                        be invalid HTML) so both surfaces stay accessible. */}
                    {ncZip && (
                      <Link
                        to={`/tools/hard-water-scorer?zip=${ncZip}`}
                        aria-label={`Score water hardness for ${nc.cityName} (ZIP ${ncZip})`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 16px 10px',
                          borderTop: `1px solid ${T.rule}`,
                          fontFamily: fontMono, fontSize: 9,
                          letterSpacing: '0.1em', textTransform: 'uppercase',
                          color: T.sienna,
                          textDecoration: 'none',
                        }}
                      >
                        <span>Score {ncZip}</span>
                        <span aria-hidden="true">→</span>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Author byline */}
        {author && (
          <div style={{
            marginTop: 56,
            paddingTop: 32,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', gap: 20, alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}>
            <div style={{
              width: 56, height: 56, flexShrink: 0,
              borderRadius: '50%',
              background: T.sienna,
              color: T.bg,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center',
              fontFamily: fontDisplay, fontSize: 24,
              fontWeight: 500,
            }}>
              {author.initial}
            </div>
            <div style={{ flex: '1 1 240px' }}>
              {/* s111: compact byline. The regional beat label ("Pacific Northwest & West
                  Coast" on Colorado pages) and the bio sentence repeated on every page in a
                  region; both stay on the contributor page. */}
              <div style={{
                fontFamily: fontBody, fontSize: 15, color: T.inkSoft,
              }}>
                Written by{' '}
                <Link to={`/contributors/${author.slug}`} style={{
                  color: T.ink, fontWeight: 600, textDecoration: 'none',
                  borderBottom: `1px solid ${T.rule}`,
                }}>
                  {author.name}
                </Link>
                {author.role ? <> · {String(author.role).split('·')[0].trim().toLowerCase().replace(/^./, (m) => m.toUpperCase())}</> : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
